import {
    Button,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Label,
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    TableCellLayout,
    type TableColumnDefinition,
} from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import { memo } from 'react';
import { graphql, usePreloadedQuery } from 'react-relay';
import { createTableColumns } from '../list/column/column-util';
import { QueryList } from '../list/QueryList';
import type { jobsQuery } from './__generated__/jobsQuery.graphql';
import type { jobsQueryFragment$data } from './__generated__/jobsQueryFragment.graphql';
import { jobsRouteApi } from './jobs-api';
import { JobsQueryCtx, useJobsQueryFragmentRef } from './jobs-query';
import { toolbarItems } from './jobs-toolbar';

export const Jobs = memo(() => {
    const jobs = usePreloadedQuery<jobsQuery>(query, jobsRouteApi.useLoaderData()).getQuartzJobs;
    const queryFragment = useJobsQueryFragmentRef(jobs);

    return (
        <JobsQueryCtx.Provider value={jobs}>
            <QueryList
                columns={jobColumns}
                items={queryFragment?.content}
                pagination={queryFragment?.pageInfo}
                routeApi={jobsRouteApi}
                // selectionMode="multiselect"
                toolbar={toolbarItems}
            />
        </JobsQueryCtx.Provider>
    );
});
Jobs.displayName = 'Jobs';

const query = graphql`
    query jobsQuery(
        $includeJobData: Boolean = true
        $pageNum: Int
        $pageSize: Int
        $sortBy: String
        $sortDir: SortDirection
        $search: String
        $searchBy: [String]
    ) {
        getQuartzJobs(
            includeJobData: $includeJobData
            pageNum: $pageNum
            pageSize: $pageSize
            sortBy: $sortBy
            sortDir: $sortDir
            search: $search
            searchBy: $searchBy
        ) {
            ...jobsQueryFragment
        }
    }
`;

type TJobs = jobsQueryFragment$data['content'][number][];
type TJob = TJobs[number];
type TTrigger = TJob['triggers'][number];

const jobColumns: TableColumnDefinition<TJob>[] = createTableColumns<TJob>(
    {
        columnId: 'name',
        renderCell: (item) => (
            // <TableCellLayout className={useNameColStyles().root} truncate={true}>
            <>
                <TriggerPopover data={item.triggers} />
                <TableCellLayout truncate={true}>{item.name}</TableCellLayout>
            </>
            // </TableCellLayout>
        ),
        renderHeaderCell: 'Name',
    },
    {
        columnId: 'group',
        renderHeaderCell: 'Gruppe',
    },
    {
        columnId: 'description',
        renderHeaderCell: 'Beschreibung',
    },
    {
        columnId: 'jobClass',
        renderHeaderCell: 'Klasse',
    }
);

interface ITriggerProps {
    data: TJob['triggers'];
}

const TriggerPopover = (props: ITriggerProps) => {
    const styles = useTriggerPopoverStyles();
    return (
        <Popover positioning="below" withArrow={true}>
            <PopoverTrigger>
                <Button
                    appearance="subtle"
                    aria-label="Pop-up mit Trigger einblenden"
                    className={styles.button}
                    icon={<InfoRegular />}
                    size="small"
                    title="Trigger einblenden"
                />
            </PopoverTrigger>

            <PopoverSurface className={styles.surface} tabIndex={-1}>
                <Label size="large">Tigger</Label>
                <TriggerGrid data={props.data} />
            </PopoverSurface>
        </Popover>
    );
};

const useTriggerPopoverStyles = makeStyles({
    button: {
        marginRight: 'var(--spacingHorizontalS)',
    },
    surface: {
        '& .fui-DataGrid': {
            marginTop: 'var(--spacingHorizontalS)',
        },
    },
});

const TriggerGrid = (props: ITriggerProps) => (
    <DataGrid columns={triggerColumns} items={props.data as TTrigger[]}>
        <DataGridHeader>
            <DataGridRow>
                {(column) => <DataGridHeaderCell>{column.renderHeaderCell()}</DataGridHeaderCell>}
            </DataGridRow>
        </DataGridHeader>

        <DataGridBody>
            {(row) => (
                <DataGridRow key={row.rowId}>
                    {(column) => <DataGridCell>{column.renderCell(row.item)}</DataGridCell>}
                </DataGridRow>
            )}
        </DataGridBody>
    </DataGrid>
);

const triggerColumns: TableColumnDefinition<TTrigger>[] = createTableColumns<TTrigger>(
    {
        columnId: 'name',
        renderHeaderCell: 'Name',
    },
    {
        columnId: 'type',
        renderHeaderCell: 'Typ',
    },
    {
        columnId: 'state',
        renderHeaderCell: 'Status',
    },
    {
        columnId: 'previousFireTime',
        renderHeaderCell: 'V. Ereignis',
    },
    {
        columnId: 'nextFireTime',
        renderHeaderCell: 'N. Ereignis',
    },
    {
        columnId: 'priority',
        renderHeaderCell: 'Prio.',
    },
    {
        columnId: 'cronExpression',
        renderHeaderCell: 'Cron-Ausdruck',
    },
    {
        columnId: 'timeZoneId',
        renderHeaderCell: 'Zeitzone',
    }
);
